import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Receipt } from "lucide-react";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { ROUTES } from "@/constants/routes";
import { invoiceService } from "../services/invoice.service";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import { InvoiceDownloadActions } from "./InvoiceDownloadActions";
import type {
  InvoicePaymentMethod,
  InvoiceResponseDTO,
  InvoiceType,
} from "../types/invoice.types";

const TYPE_LABEL: Record<InvoiceType, string> = {
  PAYMENT: "Pagamento",
  CANCELLATION: "Cancelamento",
};

const PAYMENT_METHOD_LABEL: Record<InvoicePaymentMethod, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

interface RentalInvoicesSectionProps {
  rentalId: number;
}

interface SectionState {
  rentalId: number;
  invoices: InvoiceResponseDTO[];
  totalElements: number;
  error: string | null;
}

export const RentalInvoicesSection = ({
  rentalId,
}: RentalInvoicesSectionProps) => {
  const [state, setState] = useState<SectionState | null>(null);
  // Loading derivado: enquanto o estado carregado não corresponde ao
  // rentalId atual, está carregando — dispensa setState síncrono no efeito.
  const loaded = state?.rentalId === rentalId ? state : null;
  const loading = loaded === null;
  const invoices = loaded?.invoices ?? [];
  const totalElements = loaded?.totalElements ?? 0;
  const error = loaded?.error ?? null;

  useEffect(() => {
    const controller = new AbortController();
    invoiceService
      .search(
        {
          rentalId,
          page: 0,
          size: 50,
          sortBy: "createdAt",
          sortDirection: "DESC",
        },
        { signal: controller.signal },
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setState({
          rentalId,
          invoices: response.content,
          totalElements: response.totalElements,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          rentalId,
          invoices: [],
          totalElements: 0,
          error: getApiErrorMessage(
            err,
            "Não foi possível carregar as notas fiscais.",
          ),
        });
      });
    return () => controller.abort();
  }, [rentalId]);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Receipt className="w-4 h-4" /> Notas fiscais
          {!loading && !error && <span>({totalElements})</span>}
        </div>
        <Link
          to={`${ROUTES.INVOICES}?rentalId=${rentalId}`}
          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          Ver na tela de notas
          <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-2 text-muted-foreground">
          <Spinner className="w-4 h-4" />
          <span className="text-sm">Carregando notas fiscais...</span>
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma nota fiscal para este aluguel.
        </p>
      ) : (
        <>
          {totalElements > invoices.length && (
            <p className="text-xs text-muted-foreground">
              Mostrando as {invoices.length} mais recentes — veja todas na tela
              de notas.
            </p>
          )}
          <ul className="space-y-1.5">
            {invoices.map((invoice) => (
              <li
                key={invoice.id}
                className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-2 text-sm"
              >
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">#{invoice.id}</span>
                    <InvoiceStatusBadge status={invoice.status} />
                    <span className="font-medium">
                      {formatCurrency(invoice.value)}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {TYPE_LABEL[invoice.type]} ·{" "}
                    {PAYMENT_METHOD_LABEL[invoice.paymentMethod]} · Emissão:{" "}
                    {formatDateTime(invoice.issuedAt)}
                  </span>
                </div>
                <InvoiceDownloadActions invoice={invoice} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
